/**
 * DateRangeJumpPicker
 *
 * Seletor de data adaptativo (Dia / Semana / Mês) para toolbars do calendário.
 * Renderiza um popover com o picker apropriado à view atual e devolve, via
 * `onPick`, a data normalizada correspondente ao início do intervalo.
 *
 * - Dia: retorna o próprio dia clicado.
 * - Semana: normaliza para o domingo da semana (bate com `firstDay=0` do
 *   FullCalendar usado no projeto).
 * - Mês: grade de 12 meses do ano; retorna o dia 1º do mês escolhido.
 *
 * Componente 100% controlado — não faz fetch, não mantém estado global.
 *
 * RELEASE:
 *   2026-07-25 v1.1.0 - Suporte a `renderTrigger` para permitir que o próprio
 *                        título do calendário atue como trigger do popover
 *                        (economiza espaço na toolbar). Mantém retrocompatível
 *                        o modo botão padrão usado em /tenant/activities.
 *   2026-07-24 v1.0.0 - Versão inicial (usabilidade da navegação temporal
 *   nas telas /tenant/calendar e /tenant/activities).
 */

import { useMemo, useState, type ReactNode } from 'react';
import { CalendarIcon, ChevronLeft, ChevronRight } from 'lucide-react';
import { format, startOfWeek, endOfWeek, isSameDay, isWithinInterval } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

/** Views suportadas — mesmo enum usado pelo FullCalendar no projeto. */
export type CalendarPickerView = 'dayGridMonth' | 'timeGridWeek' | 'timeGridDay';

interface DateRangeJumpPickerProps {
  /** Data âncora atualmente exibida na toolbar. */
  currentDate: Date;
  /** View atual (define qual picker é mostrado). */
  view: CalendarPickerView;
  /** Callback com a data normalizada (início do intervalo). */
  onPick: (date: Date) => void;
  /** Classe extra para o botão-trigger. */
  className?: string;
  /** Tooltip do botão. */
  ariaLabel?: string;
  /**
   * Trigger customizado. Quando fornecido, substitui o botão padrão e recebe
   * o rótulo formatado + o estado `open` do popover. Útil para transformar um
   * título já existente em trigger sem duplicar UI.
   */
  renderTrigger?: (args: { label: string; open: boolean }) => ReactNode;
}

/** Rótulo curto (~1 linha) do botão-trigger conforme a view. */
function formatTriggerLabel(date: Date, view: CalendarPickerView): string {
  switch (view) {
    case 'timeGridDay':
      return format(date, "dd 'de' MMM", { locale: ptBR });
    case 'timeGridWeek': {
      const start = startOfWeek(date, { weekStartsOn: 0 });
      const end = endOfWeek(date, { weekStartsOn: 0 });
      // Ex: "20 – 26 jul"
      const sameMonth = start.getMonth() === end.getMonth();
      if (sameMonth) {
        return `${format(start, 'dd', { locale: ptBR })} – ${format(end, "dd 'de' MMM", { locale: ptBR })}`;
      }
      return `${format(start, 'dd/MM', { locale: ptBR })} – ${format(end, 'dd/MM', { locale: ptBR })}`;
    }
    case 'dayGridMonth':
    default: {
      const label = format(date, 'MMM yyyy', { locale: ptBR });
      return label.charAt(0).toUpperCase() + label.slice(1);
    }
  }
}

/** Nomes curtos dos meses em pt-BR (Jan, Fev, ...). */
const MONTHS_PT = [
  'Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun',
  'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez',
];

export function DateRangeJumpPicker({
  currentDate,
  view,
  onPick,
  className,
  ariaLabel = 'Selecionar data',
  renderTrigger,
}: DateRangeJumpPickerProps) {
  const [open, setOpen] = useState(false);
  /** Ano exibido na grade de meses (view = mês). */
  const [monthPickerYear, setMonthPickerYear] = useState(() => currentDate.getFullYear());

  /** Rótulo compacto exibido dentro do botão. */
  const triggerLabel = useMemo(
    () => formatTriggerLabel(currentDate, view),
    [currentDate, view],
  );

  /** Intervalo da semana atualmente selecionada — usado para highlight. */
  const weekInterval = useMemo(() => {
    if (view !== 'timeGridWeek') return null;
    return {
      start: startOfWeek(currentDate, { weekStartsOn: 0 }),
      end: endOfWeek(currentDate, { weekStartsOn: 0 }),
    };
  }, [view, currentDate]);

  /** Handler quando o usuário escolhe um dia (view dia ou semana). */
  const handleSelectDate = (date: Date | undefined) => {
    if (!date) return;
    if (view === 'timeGridWeek') {
      onPick(startOfWeek(date, { weekStartsOn: 0 }));
    } else {
      onPick(date);
    }
    setOpen(false);
  };

  /** Handler quando o usuário escolhe um mês na grade. */
  const handleSelectMonth = (monthIdx: number) => {
    onPick(new Date(monthPickerYear, monthIdx, 1));
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        {renderTrigger ? (
          renderTrigger({ label: triggerLabel, open })
        ) : (
          <Button
            type="button"
            variant="outline"
            size="sm"
            aria-label={ariaLabel}
            title={ariaLabel}
            className={cn(
              'h-9 gap-1.5 px-2.5 text-xs font-medium capitalize',
              className,
            )}
          >
            <CalendarIcon className="h-4 w-4 text-muted-foreground" />
            <span className="hidden md:inline">{triggerLabel}</span>
          </Button>
        )}
      </PopoverTrigger>

      <PopoverContent
        align="start"
        className={cn(
          'p-0 pointer-events-auto',
          view === 'dayGridMonth' ? 'w-[260px]' : 'w-auto',
        )}
      >
        {view === 'dayGridMonth' ? (
          /* -------- Picker MÊS: navegação por ano + grade 3x4 de meses -------- */
          <div className="p-3">
            <div className="flex items-center justify-between mb-2">
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                onClick={() => setMonthPickerYear((y) => y - 1)}
                aria-label="Ano anterior"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="text-sm font-semibold">{monthPickerYear}</span>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                onClick={() => setMonthPickerYear((y) => y + 1)}
                aria-label="Próximo ano"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>

            <div className="grid grid-cols-3 gap-1.5">
              {MONTHS_PT.map((label, idx) => {
                const isSelected =
                  monthPickerYear === currentDate.getFullYear() &&
                  idx === currentDate.getMonth();
                const now = new Date();
                const isCurrent =
                  monthPickerYear === now.getFullYear() && idx === now.getMonth();
                return (
                  <Button
                    key={label}
                    type="button"
                    variant={isSelected ? 'default' : 'ghost'}
                    size="sm"
                    onClick={() => handleSelectMonth(idx)}
                    className={cn(
                      'h-8 text-xs',
                      !isSelected && isCurrent && 'ring-1 ring-primary/40',
                    )}
                  >
                    {label}
                  </Button>
                );
              })}
            </div>
          </div>
        ) : (
          /* -------- Picker DIA / SEMANA: shadcn Calendar com modifiers -------- */
          <Calendar
            mode="single"
            locale={ptBR}
            weekStartsOn={0}
            selected={currentDate}
            defaultMonth={currentDate}
            onSelect={handleSelectDate}
            initialFocus
            className="p-3 pointer-events-auto"
            modifiers={
              weekInterval
                ? {
                    selectedWeek: (d: Date) =>
                      isWithinInterval(d, weekInterval) ||
                      isSameDay(d, weekInterval.start) ||
                      isSameDay(d, weekInterval.end),
                  }
                : undefined
            }
            modifiersClassNames={
              weekInterval
                ? {
                    selectedWeek:
                      'bg-primary/15 text-primary-foreground/90 rounded-none',
                  }
                : undefined
            }
          />
        )}
      </PopoverContent>
    </Popover>
  );
}
